import { absoluteRouteUrl, buildPlayPath } from '../navigation/appRoutes';

export interface ScoreChallenge {
  tableId: string;
  score: number;
  challenger: string | null;
}

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'unavailable';

const MAX_CHALLENGE_SCORE = 2_000_000_000;

function safeScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(MAX_CHALLENGE_SCORE, Math.floor(value)));
}

function urlForTable(tableId: string, baseHref: string): URL {
  return new URL(absoluteRouteUrl(buildPlayPath(tableId), baseHref));
}

export function buildTableShareUrl(
  tableId: string,
  baseHref = typeof window === 'undefined' ? 'https://ballsofpin.com/' : window.location.href,
): string {
  return urlForTable(tableId, baseHref).toString();
}

export function buildScoreChallengeUrl(
  tableId: string,
  score: number,
  challenger?: string | null,
  baseHref = typeof window === 'undefined' ? 'https://ballsofpin.com/' : window.location.href,
): string {
  const url = urlForTable(tableId, baseHref);
  url.searchParams.set('challenge', String(safeScore(score)));
  const name = challenger?.trim().slice(0, 40);
  if (name) url.searchParams.set('from', name);
  return url.toString();
}

export function parseScoreChallenge(search: string): ScoreChallenge | null {
  const params = new URLSearchParams(search);
  const tableId = params.get('table')?.trim();
  const rawScore = params.get('challenge');
  if (!tableId || rawScore === null || rawScore.trim() === '') return null;

  const score = Number(rawScore);
  if (!Number.isFinite(score) || !Number.isInteger(score) || score < 0 || score > MAX_CHALLENGE_SCORE) return null;

  const rawChallenger = params.get('from')?.trim() ?? '';
  return {
    tableId,
    score,
    challenger: rawChallenger ? rawChallenger.slice(0, 40) : null,
  };
}

export async function shareUrl(input: { title: string; text: string; url: string }): Promise<ShareResult> {
  if (typeof navigator === 'undefined') return 'unavailable';

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share(input);
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
      // Native sharing can reject for platform-specific reasons. Falling back
      // to the clipboard keeps sharing useful without surfacing an app error.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(input.url);
      return 'copied';
    } catch {
      // Continue to the explicit unavailable result below.
    }
  }

  return 'unavailable';
}
